/**
 * Admin Panel API client factory.
 *
 * Builds a single `@retail-ops/api-client` `ApiClient` wired against the
 * mypcb-tenant MSAL token + base URL. NO Teams SDK — admin is browser-only.
 *
 * Per Bible §3.0: function key is sent in `x-functions-key` header only.
 */

import { ApiClient } from '@retail-ops/api-client';
import { getRetailToken } from '@/auth/tokens';
import { APP_VERSION } from '@/constants/version';

const CLIENT_ID = 'admin-panel';
const CLIENT_VERSION = `admin-panel@${APP_VERSION}`;

export interface ApiClientFactoryOptions {
  baseUrl: string;
  functionKey: string;
  clientId?: string;
}

export function createApiClient(options: ApiClientFactoryOptions): ApiClient {
  const clientId = options.clientId ?? CLIENT_ID;
  return new ApiClient({
    baseUrl: options.baseUrl,
    functionKey: options.functionKey,
    version: CLIENT_VERSION,
    getToken: async () => {
      try {
        // Shared backend token when BT has granted consent, else the ID token (auth/tokens.ts).
        return await getRetailToken();
      } catch {
        return undefined;
      }
    },
    context: () => ({ clientId }),
  });
}

export function apiClientFactoryOptionsFromEnv(): ApiClientFactoryOptions {
  const baseUrl =
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  const functionKey =
    (import.meta.env.VITE_FUNCTION_KEY as string | undefined) ?? '';

  if (!baseUrl && import.meta.env.PROD) {
    // eslint-disable-next-line no-console
    console.error(
      '[admin-panel] VITE_API_BASE_URL is not set. The app cannot reach the Function.',
    );
  }

  return {
    baseUrl: baseUrl || 'https://pcb-retail-api.azurewebsites.net/api',
    functionKey,
  };
}
